import { authenticateWithProvider } from './ProviderAuth.js'
import { hasNativeMessageBridge, hasNativeCapability } from './NativeEnvironment.js'
import { isolatedAccountAuth } from './AccountMerge.js'

export const canSignInWithApple = () => !hasNativeMessageBridge() || hasNativeCapability('appleSignIn')

const appleProvider = firebase => {
  const provider = new firebase.auth.OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')
  return provider
}

export const authenticateWithApple = async options => {
  const current = () => !options.linkUser || options.firebase.auth().currentUser?.uid === options.linkUser.uid
  if (!current()) return { error: accountChanged() }
  const result = await authenticateWithProvider({
    ...options,
    providerName: 'Apple',
    createProvider: () => appleProvider(options.firebase)
  })
  if (!current()) return { error: accountChanged() }
  if (result?.error?.code === 'auth/account-exists-with-different-credential') {
    return { error: Object.assign(new Error('Sign in with your existing method, then link Apple from Account to keep your Tokens and saved commands.'), { code: result.error.code }) }
  }
  return result
}

const accountChanged = () => Object.assign(new Error('The IntelliKey account changed. Please try again.'), {
  code: 'auth/user-changed'
})

// Firebase holds the Apple private key. Only a fresh, account-bound Apple
// credential is sent to Firebase's revocation endpoint before server deletion.
export const revokeAppleForAccountDeletion = async ({ firebase, nativeRevoke, linkedAccounts = [], isCurrent = () => true, fetchImpl = (...args) => fetch(...args) }) => {
  const auth = firebase.auth()
  const user = auth.currentUser
  if (!user?.uid) throw accountChanged()
  const current = () => isCurrent() && auth.currentUser?.uid === user.uid
  const check = () => { if (!current()) throw accountChanged() }
  check()
  for (const identity of linkedAccounts.filter(identity => identity.providerId === 'apple.com' && identity.accountUID !== user.uid)) {
    if (nativeRevoke) {
      const result = await nativeRevoke({ expectedUID: user.uid, appleAccountUID: identity.accountUID })
      check()
      if (result?.cancelled) return false
      if (!result?.ok || result.uid !== user.uid) throw new Error(result?.error || 'Could not revoke the linked Apple account.')
    } else {
      const session = await isolatedAccountAuth(firebase)
      try {
        const result = await session.auth.signInWithPopup(appleProvider(firebase))
        check()
        if (result?.user?.uid !== identity.accountUID || !result.credential?.accessToken) {
          if (result?.additionalUserInfo?.isNewUser) await result.user.delete()
          throw accountChanged()
        }
        const idToken = await result.user.getIdToken(true)
        check()
        const response = await fetchImpl(`https://identitytoolkit.googleapis.com/v2/accounts:revokeToken?key=${encodeURIComponent(firebase.app().options.apiKey)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
          body: JSON.stringify({ providerId: 'apple.com', tokenType: 'ACCESS_TOKEN', token: result.credential.accessToken, idToken })
        })
        check()
        if (!response.ok) throw new Error('Could not revoke the linked Apple account.')
      } catch (error) {
        if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(error.code)) return false
        throw error
      } finally { await session.close() }
    }
  }
  if (!user.providerData?.some(provider => provider.providerId === 'apple.com')) return true

  if (nativeRevoke) {
    const result = await nativeRevoke({ expectedUID: user.uid })
    check()
    if (result?.cancelled) return false
    if (result?.ok !== true || result.uid !== user.uid) {
      throw new Error(result?.error || 'Could not confirm your Apple account for deletion.')
    }
    return true
  }

  let result
  try {
    result = await user.reauthenticateWithPopup(appleProvider(firebase))
  } catch (error) {
    if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(error.code)) return false
    throw error
  }
  check()
  const accessToken = result?.credential?.accessToken
  if (result?.user?.uid !== user.uid || !accessToken) {
    throw new Error('Could not confirm your Apple account for deletion.')
  }
  const idToken = await user.getIdToken(true)
  check()
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30000)
  try {
    const response = await fetchImpl(`https://identitytoolkit.googleapis.com/v2/accounts:revokeToken?key=${encodeURIComponent(firebase.app().options.apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      signal: controller.signal,
      body: JSON.stringify({ providerId: 'apple.com', tokenType: 'ACCESS_TOKEN', token: accessToken, idToken })
    })
    check()
    if (!response.ok) throw new Error('Could not revoke Apple access. Your IntelliKey account has not been deleted.')
    return true
  } finally {
    clearTimeout(timeout)
  }
}
